import type { CameraPose } from '../../data/types';
import type { Vec2, Vec3 } from '../../geo/types';
import { WORLD } from '../../core/config';
import {
  derivePainterProjectionParams,
  farMapJets,
  STYLE_CONSTANTS,
  type DerivedPainterParams,
  type Jet,
} from '../../render/painterProjection';
import { painterFrameFromView, type PainterFrame } from '../../render/painterWarp';

/*
 * Up to d0 the painter view draws a straight edge straight; past it the far map bends it. Along an
 * edge whose lateral offset from the painter's foot is x = m d + A at forward distance d, the far map
 * puts the ground D(d) ahead and D (m + A/d + shear(d)) right, and scales its height by D/d, so the
 * camera draws it on a row v(d) and in the column u0 + m g1 + A g2 + g3, the g of the row alone. Over
 * a piece of the edge the image then strays along the rows from the chord between the piece's ends by
 * at most (Δv)²/8 (|m| max|g1''| + |A| max|g2''| + max|g3''|), the curvatures taken along the row.
 */

/** |g1''|, |g2''| and |g3''| at t metres past d0 with their rounding, and bounds on them over a stretch. */
export interface FarCurvatures {
  at(t: number): Vec3;
  over(t0: number, t1: number): Vec3;
}

/**
 * How far the painter view's image of an edge, before the residual shift, strays from the segment
 * between `ends`, where it renders the edge's ends. Given `settle`, the edge is refined only while
 * that may bring the bound down to it: a piece whose bound is within it stays whole, and once a
 * point of the image lies farther from the segment nothing more is split. The result bounds the arc
 * either way, and passes `settle` only where refining in full would too.
 */
export interface FarArc {
  arcPx(a: Vec2, b: Vec2, ends: readonly [Vec2, Vec2], settle?: number): number;
}

interface Camera {
  readonly focalPx: number;
  readonly cosPitch: number;
  readonly sinPitch: number;
  // The eye's height above the painter's foot.
  readonly eyeHeight: number;
  // +1 where the camera's right is the far map's, -1 where it is the opposite.
  readonly handed: number;
}

const AXIS_TOLERANCE = 1e-9;
// The jets' relative rounding, generously: where a curvature vanishes, as g1's does on the ground,
// whose lines through the foot the far map keeps straight, its samples are rounding alone.
const JET_ROUNDING = 1e-12;
// Bins run a fortieth of the knee long over its first five lengths, then grow by a percent each out
// to twice the camera's far plane; each is sampled at five points, and its bound carries a tenth more.
const FINE_BINS_PER_KNEE = 40;
const FINE_KNEES = 5;
const BIN_GROWTH = 1.01;
const REACH_M = 2 * WORLD.cameraFar;
const SAMPLES_PER_BIN = 5;
const BIN_MARGIN = 1.1;
// A piece of an edge is split while its bound on straying from its chord passes this, up to
// 2^MAX_SPLITS pieces.
const PIECE_STRAY_PX = 0.05;
const MAX_SPLITS = 6;

function times(k: number, [a, a1, a2]: Jet): Jet {
  return [k * a, k * a1, k * a2];
}

function plus(p: Jet, q: Jet): Jet {
  return [p[0] + q[0], p[1] + q[1], p[2] + q[2]];
}

function product([a, a1, a2]: Jet, [b, b1, b2]: Jet): Jet {
  return [a * b, a1 * b + a * b1, a2 * b + 2 * a1 * b1 + a * b2];
}

function quotient([a, a1, a2]: Jet, [b, b1, b2]: Jet): Jet {
  const q = a / b;
  const q1 = (a1 - q * b1) / b;
  return [q, q1, (a2 - 2 * q1 * b1 - q * b2) / b];
}

function cameraOf(frame: PainterFrame, params: DerivedPainterParams): Camera {
  const [fx, fz] = [frame.forward[0], frame.forward[2]];
  const level = Math.hypot(fx, fz);
  const along = (fx * params.forwardDir[0] + fz * params.forwardDir[2]) / level;
  const handed = frame.right[0] * params.rightDir[0] + frame.right[2] * params.rightDir[2];
  const footed = frame.eye[0] === params.groundPoint[0] && frame.eye[2] === params.groundPoint[2];
  if (!footed || Math.abs(frame.right[1]) > AXIS_TOLERANCE || 1 - along > AXIS_TOLERANCE) {
    throw new Error('The painter camera must stand over the far map foot and look along its axis');
  }
  if (1 - Math.abs(handed) > AXIS_TOLERANCE) {
    throw new Error('The painter camera must not roll against the far map axes');
  }
  return {
    focalPx: frame.focalPx,
    cosPitch: level,
    sinPitch: -frame.forward[1],
    eyeHeight: frame.eye[1] - params.groundPoint[1],
    handed: Math.sign(handed),
  };
}

// The row v and the g functions at forward distance d past d0, as jets along d, on the plane planeY.
function farImageJets(d: number, params: DerivedPainterParams, camera: Camera, planeY: number) {
  const { forward, shear } = farMapJets(d, params);
  const along: Jet = [d, 1, 0];
  const lift = planeY - params.groundPoint[1];
  const drop = plus([camera.eyeHeight, 0, 0], times(-lift, quotient(forward, along)));
  const depth = plus(times(camera.cosPitch, forward), times(camera.sinPitch, drop));
  const up = plus(times(camera.sinPitch, forward), times(-camera.cosPitch, drop));
  const lateral = times(camera.handed * camera.focalPx, quotient(forward, depth));
  return {
    row: times(-camera.focalPx, quotient(up, depth)),
    g: [lateral, quotient(lateral, along), product(lateral, shear)],
  };
}

function curvaturesAt(
  t: number,
  params: DerivedPainterParams,
  camera: Camera,
  planeY: number
): Vec3 {
  const { row, g } = farImageJets(params.d0 + t, params, camera, planeY);
  const [, v1, v2] = row;
  if (!(v1 < 0))
    throw new Error(`The far map must draw farther ground higher (t = ${String(t)} m)`);
  const along = ([, g1, g2]: Jet) =>
    (Math.abs(g2 * v1 - g1 * v2) + JET_ROUNDING * (Math.abs(g2 * v1) + Math.abs(g1 * v2))) /
    Math.abs(v1) ** 3;
  return [along(g[0]!), along(g[1]!), along(g[2]!)];
}

function largest(vectors: readonly Vec3[]): Vec3 {
  const most = (k: 0 | 1 | 2) => Math.max(0, ...vectors.map((vector) => vector[k]));
  return [most(0), most(1), most(2)];
}

function binEdges(params: DerivedPainterParams): number[] {
  const fine = params.farKneeM / FINE_BINS_PER_KNEE;
  const edges = [0];
  while (edges.at(-1)! < FINE_KNEES * params.farKneeM) edges.push(edges.at(-1)! + fine);
  while (edges.at(-1)! < REACH_M - params.d0) edges.push(edges.at(-1)! * BIN_GROWTH);
  return edges;
}

/** The curvatures of the far map's image along the row on the plane at planeY, and their bins. */
export function createFarCurvatures(
  params: DerivedPainterParams,
  frame: PainterFrame,
  planeY: number
): FarCurvatures {
  const camera = cameraOf(frame, params);
  const at = (t: number) => curvaturesAt(t, params, camera, planeY);
  const edges = binEdges(params);
  const bins = edges.slice(1).map((end, i): Vec3 => {
    const start = edges[i]!;
    const samples = Array.from({ length: SAMPLES_PER_BIN }, (_, k) =>
      at(start + ((end - start) * k) / (SAMPLES_PER_BIN - 1))
    );
    const [c1, c2, c3] = largest(samples);
    return [c1 * BIN_MARGIN, c2 * BIN_MARGIN, c3 * BIN_MARGIN];
  });
  const binOf = (t: number) => {
    if (!(t >= 0 && t <= edges.at(-1)!))
      throw new RangeError(`No far curvature bin at ${String(t)} m`);
    let [low, high] = [0, bins.length - 1];
    while (low < high) {
      const mid = (low + high + 1) >> 1;
      if (edges[mid]! <= t) low = mid;
      else high = mid - 1;
    }
    return low;
  };
  const levels = runMaxima(bins);
  return {
    at,
    over(t0, t1) {
      const [first, last] = [binOf(t0), binOf(t1)];
      const level = Math.floor(Math.log2(last - first + 1));
      const [runs, other] = [levels[level]!, 3 * (last - (1 << level) + 1)];
      return [
        Math.max(runs[3 * first]!, runs[other]!),
        Math.max(runs[3 * first + 1]!, runs[other + 1]!),
        Math.max(runs[3 * first + 2]!, runs[other + 2]!),
      ];
    },
  };
}

// The bins' bounds, and their largest over every run of 2, 4, 8 ... bins, so that a stretch takes
// the larger of two runs that cover it.
function runMaxima(bins: readonly Vec3[]): Float64Array[] {
  const levels = [Float64Array.from(bins.flat())];
  for (let run = 1; 2 * run <= bins.length; run *= 2) {
    const shorter = levels.at(-1)!;
    const longer = new Float64Array(3 * (bins.length - 2 * run + 1));
    longer.forEach((_, i) => (longer[i] = Math.max(shorter[i]!, shorter[i + 3 * run]!)));
    levels.push(longer);
  }
  return levels;
}

function distanceToSegment(p: Vec2, [a, b]: readonly [Vec2, Vec2]): number {
  const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
  const span = dx * dx + dy * dy;
  const t =
    span > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / span)) : 0;
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
}

interface Piece {
  readonly from: number;
  readonly to: number;
  readonly start: Vec2;
  readonly end: Vec2;
  readonly splits: number;
}

// An edge from a to b past d0: its point s of the way along, how far past d0 that point lies, the
// weights |m|, |A| and 1 of its curvatures, and where the painter view renders its ends.
interface FarEdge {
  readonly pointAt: (s: number) => Vec2;
  readonly pastAt: (s: number) => number;
  readonly weights: Vec3;
  readonly ends: readonly [Vec2, Vec2];
}

// The edge from `first.from` to `first.to` of the way along it: pieces split until each strays from
// its chord by at most PIECE_STRAY_PX, or until `settle` has been decided, each bounded by its ends'
// distance from the drawn segment and that stray.
function farArcOf(
  edge: FarEdge,
  renderedAt: (p: Vec2) => Vec2,
  curvatures: FarCurvatures,
  first: Piece,
  settle: number | undefined
): number {
  const { weights, ends } = edge;
  let [arc, reached] = [0, 0];
  const pieces = [first];
  for (let piece = pieces.pop(); piece; piece = pieces.pop()) {
    const [t0, t1] = [edge.pastAt(piece.from), edge.pastAt(piece.to)].map((t) => Math.max(0, t));
    const bound = curvatures.over(Math.min(t0!, t1!), Math.max(t0!, t1!));
    const rows = piece.end[1] - piece.start[1];
    const stray = ((rows * rows) / 8) * (weights[0] * bound[0] + weights[1] * bound[1] + bound[2]);
    const reach = Math.max(
      distanceToSegment(piece.start, ends),
      distanceToSegment(piece.end, ends)
    );
    reached = Math.max(reached, reach);
    const settled = settle !== undefined && (reach + stray <= settle || reached > settle);
    if (settled || stray <= PIECE_STRAY_PX || piece.splits >= MAX_SPLITS) {
      arc = Math.max(arc, reach + stray);
      continue;
    }
    const middle = (piece.from + piece.to) / 2;
    const at = renderedAt(edge.pointAt(middle));
    const splits = piece.splits + 1;
    pieces.push({ from: piece.from, to: middle, start: piece.start, end: at, splits });
    pieces.push({ from: middle, to: piece.to, start: at, end: piece.end, splits });
  }
  return arc;
}

/**
 * The far map's arc for the painter view at `view` on the plane at planeY, where `renderedAt` gives
 * the pixel the view renders a layout point at before the residual shift.
 */
export function createFarArc(
  view: CameraPose,
  planeY: number,
  renderedAt: (p: Vec2) => Vec2
): FarArc {
  const params = derivePainterProjectionParams(view, STYLE_CONSTANTS);
  const curvatures = createFarCurvatures(
    params,
    painterFrameFromView(view, STYLE_CONSTANTS),
    planeY
  );
  const offsets = (p: Vec2): Vec2 => {
    const [dx, dz] = [p[0] - params.groundPoint[0], -p[1] - params.groundPoint[2]];
    return [
      dx * params.forwardDir[0] + dz * params.forwardDir[2],
      dx * params.rightDir[0] + dz * params.rightDir[2],
    ];
  };
  return {
    arcPx(a, b, ends, settle) {
      const [[da, xa], [db, xb]] = [offsets(a), offsets(b)];
      if (Math.max(da, db) <= params.d0 || da === db) return 0;
      const slope = (xb - xa) / (db - da);
      const edge: FarEdge = {
        pointAt: (s) => [a[0] + s * (b[0] - a[0]), a[1] + s * (b[1] - a[1])],
        pastAt: (s) => da + s * (db - da) - params.d0,
        weights: [Math.abs(slope), Math.abs(xa - slope * da), 1],
        ends,
      };
      if (Math.min(da, db) >= params.d0) {
        const whole = { from: 0, to: 1, start: ends[0], end: ends[1], splits: 0 };
        return farArcOf(edge, renderedAt, curvatures, whole, settle);
      }
      // The part nearer than d0 is drawn straight to where the edge crosses d0.
      const knee = (params.d0 - da) / (db - da);
      const at = renderedAt(edge.pointAt(knee));
      const far =
        da < db
          ? { from: knee, to: 1, start: at, end: ends[1], splits: 0 }
          : { from: 0, to: knee, start: ends[0], end: at, splits: 0 };
      return farArcOf(edge, renderedAt, curvatures, far, settle);
    },
  };
}
